import React, { useEffect } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useSelector, shallowEqual } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CustomImageHandler } from '../../components/CustomImageHandler';
import { User } from '../../../model/user/User';
import { openCalendlyPopup } from '../../functions/Functions';
import { IAllStates } from '../../../store/rootReducer';

const selectUserAndAgency = (state: IAllStates) => ({
    user: state.auth.user,
    company: state.auth.user?.agencyCompany,
});

const CompanyInfo: React.FC<{ company: User }> = ({ company }) => {
    const { t } = useTranslation();
    if (!company?.id) {
        return null;
    }
    return (
        <>
            <div className="ml-5 d-flex flex-column align-items-end px-3">
                <span className="mr-1 text-muted">{t('influencer.header.managedBy')}:</span>
                <span className="font-weight-semi-bold">{company.displayName ?? '-'}</span>
            </div>
            <CustomImageHandler
                photoPath={company.profilePhoto?.thumbnail as string}
                classes="campaign-icon"
                altTag="User Profile"
                name={company?.displayName ?? '-'}
            />
        </>
    );
};

const CreatorTeam = () => {
    const { t } = useTranslation();

    useEffect(() => {
        const script = document.createElement('script');
        script.src = 'https://assets.calendly.com/assets/external/widget.js';
        script.async = true;
        document.body.appendChild(script);

        return () => {
            document.body.removeChild(script);
        };
    }, []);

    return (
        <div className="d-flex align-items-center">
            <div
                onClick={() => openCalendlyPopup(String(process.env.VITE_CREATOR_SUCCESS_CALENDLY))}
                className="d-flex flex-column align-items-end px-3"
            >
                <h6 className="mb-0 text-primary cursor-pointer">Creator Success Team</h6>
                <div className="btn-link word-break font-11 text-body cursor-pointer">
                    {t('influencer.header.book_meeting_creator_success')}
                </div>
            </div>
            <CustomImageHandler
                photoPath={'/tempAssets/creatorsuccessteam.jpg'}
                classes="campaign-icon"
                altTag="User Profile"
            />
        </div>
    );
};

const InfluencerHeader: React.FC = () => {
    const { t } = useTranslation();
    const { user, company } = useSelector(selectUserAndAgency, shallowEqual);
    const location = useLocation();
    const isOnCampaignsPage = location.pathname === '/influencer/campaigns';

    return (
        <Row id="infuencer-header" className="d-none d-md-flex mb-3 justify-content-between">
            <Col className="col-auto d-flex justify-content-center flex-column">
                <h4>
                    {t('influencer.header.welcome_back')} {user?.displayName || user?.firstName}!
                </h4>
                {!isOnCampaignsPage && (
                    <Link to="/influencer/campaigns" className="text-uppercase text-muted text-underline ">
                        {t('influencer.header.myCampaigns')} ({user?.influencerCampaigns ?? '...'})
                    </Link>
                )}
            </Col>
            <Col className="col-auto d-flex align-items-center justify-content-end">
                <CreatorTeam />
                <CompanyInfo company={company} />
            </Col>
        </Row>
    );
};

export default React.memo(InfluencerHeader);
